import React, { FC, useState, useMemo, memo } from 'react';
import { utils, writeFile } from 'xlsx';
import { useTranslate } from '@im/react-localization';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Tag } from '@im/ui-lib/Tag';
import SelectOption from '../../../components/SelectOption';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterIcon from '@mui/icons-material/FilterList';
import LoadingButton from '@mui/lab/LoadingButton';
import { FormFieldProps } from 'src/components/Form';
import { Customer } from '../../../hooks/customerSearchQuery';
import { NewMember } from './types';
import Grid, {
  ColumnDef,
  GroupedRow,
} from '../../../components/DataGridVirtual';
import { CellContext } from '@tanstack/react-table';
import { NoRowsOverlay } from './util/SharedComponents';

function downloadExcel(data: Record<string, any>[], filename: string) {
  const ws = utils.json_to_sheet(data);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws);
  writeFile(wb, `${filename}.xlsx`);
}

const groupBy = (customer: Customer) =>
  customer.masterCustomerNumber?.toString();

const isParent = (customer: Customer) =>
  customer.masterCustomerNumber?.toString() ===
  customer.customerNumber?.toString();

const FILTERS = {
  all: 'all',
  added: 'added', // eslint-disable-line sort-keys
  error: 'error',
};

export interface CustomerGroupMemberListProps
  extends Pick<FormFieldProps<any>, 'error' | 'helperText'> {
  addRelated: (customers: Customer['id'][]) => Promise<any>;
  onRemove: (customers: Customer['id'][]) => void;
  rows: Customer[];
}

export type { Customer };

const CustomerGroupMemberList: FC<CustomerGroupMemberListProps> = ({
  addRelated,
  error,
  helperText,
  onRemove,
  rows,
}) => {
  const translate = useTranslate();

  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [filter, setFilter] = useState<any>('all');

  const filters = Object.values(FILTERS).map((value) => ({
    label: translate(`CustomerGroups.addCustomersFilter${value.toUpperCase()}`),
    value,
  }));

  const [pendingActions, setPendingActions] = useState<any>({});

  const cellStatus = ({ getValue }: CellContext<GroupedRow<NewMember>, any>) =>
    getValue() ? (
      <Tag
        label={translate('CustomerGroups.addCustomersStatusAdded')}
        variant="success"
      />
    ) : (
      <Tag
        label={translate('CustomerGroups.addCustomersStatusError')}
        variant="error"
      />
    );
  const cellAddRelated = ({ row }: CellContext<GroupedRow<NewMember>, any>) => (
    <LoadingButton
      disabled={!row.original.customerName}
      loading={pendingActions[row.id]}
      onClick={() => {
        setPendingActions({ ...pendingActions, [row.id]: true });
        addRelated([row.id]).then(() =>
          setPendingActions({ ...pendingActions, [row.id]: false })
        );
      }}
      title={translate('CustomerGroups.addCustomersAddRelated')}
      variant="newText"
    >
      <AccountTreeIcon fontSize="small" />
    </LoadingButton>
  );
  const cellCustomerName = ({
    getValue,
    row,
  }: CellContext<GroupedRow<NewMember>, any>) => {
    const value = getValue();
    const textProps = {
      component: 'span',
      fontSize: 'inherit',
      ...(!value && { fontStyle: 'italic' }),
      sx: {
        ...(!value && { opacity: 0.6 }),
        ...(row.original.child && { paddingLeft: 2 }),
      },
    };
    return (
      <>
        <Typography {...textProps}>
          {value || translate('CustomerGroups.customerNotFound')}
        </Typography>
        {row.original.related && (
          <Tag label="Related" sx={{ marginLeft: 1 }} variant="default" />
        )}
      </>
    );
  };
  const cellRemove = ({ row }: CellContext<GroupedRow<NewMember>, any>) => (
    <Button
      onClick={() => onRemove([row.id])}
      title={translate('CustomerGroups.removeCustomer')}
      variant="newText"
    >
      <DeleteOutlinedIcon fontSize="small" />
    </Button>
  );
  const columns = useMemo<ColumnDef<GroupedRow<NewMember>, any>[]>(
    () => [
      {
        accessorFn: (customer) => customer.customerName,
        cell: cellCustomerName,
        enableSorting: false,
        header: translate('CustomerGroups.customerName'),
        id: 'customerName',
      },
      {
        accessorFn: (customer) => customer.customerNumber,
        enableSorting: false,
        header: translate('CustomerGroups.customerNumber'),
        id: 'customerNumber',
        size: 50,
      },
      {
        accessorFn: (customer) => !!customer.customerName,
        cell: cellStatus,
        header: translate('CustomerGroups.status'),
        id: 'status',
        size: 40,
      },
      {
        cell: cellAddRelated,
        enableSorting: false,
        id: 'addRelated',
        size: 20,
      },
      {
        cell: cellRemove,
        enableSorting: false,
        id: 'remove',
        size: 20,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [translate, onRemove, addRelated, pendingActions, setPendingActions]
  );

  const errors = useMemo(() => rows.filter((c) => !c.customerName), [rows]);

  const filteredRows = useMemo(() => {
    const filterMap = {
      [FILTERS.all]: () => rows,
      [FILTERS.added]: () => rows.filter((c) => !!c.customerName),
      [FILTERS.error]: () => rows.filter((c) => !c.customerName),
    };
    return filterMap[filter]();
  }, [rows, filter]);

  const noRowsOverlay = NoRowsOverlay({
    defaultText: translate('CustomerGroups.addCustomersPlaceholderText'),
    defaultTitle: translate('CustomerGroups.addCustomersPlaceholderTitle'),
    error,
    filterCondition: filter !== FILTERS.all && rows.length,
    helperText,
    noRecords: translate('Common.NoRecord'),
  });

  const getNoRows = () => noRowsOverlay;
  return (
    <Stack flexGrow={1} overflow="hidden" spacing={1.5}>
      <Stack
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
      >
        <Typography
          sx={(theme) => ({ fontWeight: theme.typography.fontWeightSemiBold })}
          variant="body1"
        >
          {translate('CustomerGroups.addCustomersCount', {
            count: rows.length,
          })}
        </Typography>
        <Stack flexDirection="row" gap={2}>
          <LoadingButton
            disabled={selectedCustomers.length === 0}
            loading={pendingActions.selected}
            onClick={() => {
              setPendingActions({ ...pendingActions, selected: true });
              addRelated(selectedCustomers).then(() =>
                setPendingActions({ ...pendingActions, selected: false })
              );
            }}
            startIcon={<AccountTreeIcon />}
            variant="newText"
          >
            {translate('CustomerGroups.addCustomersAddRelated')}
          </LoadingButton>
          <Button
            disabled={selectedCustomers.length === 0}
            onClick={() => onRemove(selectedCustomers)}
            startIcon={<DeleteOutlinedIcon />}
            variant="newText"
          >
            {translate('Common.Remove')}
          </Button>
          <Button
            disabled={!errors.length}
            onClick={() =>
              downloadExcel(
                errors.map((c) => ({ 'CUSTOMER NUMBER': c.customerNumber })),
                translate('CustomerGroups.downloadCustomersDefaultFilename')
              )
            }
            startIcon={<DownloadIcon />}
            variant="newText"
          >
            {translate('CustomerGroups.addCustomersErrorReport')}
          </Button>
          <SelectOption
            onChange={(e) => {
              setFilter(e.target.value as string);
            }}
            options={filters}
            renderValue={(value) => {
              return (
                <Stack alignItems="center" flexDirection="row" gap={1}>
                  <FilterIcon />
                  {filters.find((opt) => opt.value === value)?.label}
                </Stack>
              );
            }}
            style={{ width: '175px' }}
            value={filter}
          ></SelectOption>
        </Stack>
      </Stack>
      <Grid<Customer>
        NoRowsOverlay={getNoRows}
        checkboxSelection
        columns={columns}
        getRowId={(customer: Customer) => customer.id?.toString()}
        groupBy={groupBy}
        isParent={isParent}
        onSelectionModelChange={setSelectedCustomers}
        rows={filteredRows || []}
        selectionModel={selectedCustomers}
        sortBy={[{ desc: false, id: 'status' }]}
        stickyHeader
      />
    </Stack>
  );
};

export default memo(CustomerGroupMemberList);
